
import { useCallback, useEffect, useState } from "react";
import AccessSideDrawer from "./AccessSideDrawer";
import { getEmployeeList } from "../../EmployeeList/Service/employeeService";


const PAGE_SIZE = 10;

const EmployeesList = () => {
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [sideDrawer, setSideDrawer] = useState(null);

  // Read the logged-in user's information.
  const getLoggedInUser = () => {
    try {
      return JSON.parse(localStorage.getItem("User") || "{}");
    } catch {
      return {};
    }
  };

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    setError("");

    const user = getLoggedInUser();

    const payload = {
      OrganisationId: user.OrganisationId ?? user.OrgId ?? 0,
      LoggedInEmployeeId:
        user.EmployeeId ?? user.EmpId ?? 0,
      IsDirectReportOrHierarchy: 0,
      EmployeeId: 0,
      SearchText: search.trim(),
      FollowersOnly: 0,
      FollowingsOnly: 0,
      IsSuggestedOnly: 0,
      IsEmployeesAlso: 0,
      pageNumber,
      pageSize: PAGE_SIZE,
      ZoneId: 0,
      RegionId: 0,
      ProjectId: 0,
    };

    try {
      const result = await getEmployeeList(payload);

      console.log("Employee List API response:", result);

      // Adjust these paths to match your actual API response.
      const data =
        result?.Data;

      const list = Array.isArray(data)
        ? data
        : [];

      setEmployees(list);

      setTotalCount(
        data[0]?.totalEmpCount 
      );
    } catch (err) {
      console.error("Failed to fetch employees:", err);

      setEmployees([]);
      setTotalCount(0);
      setError(
        err?.response?.data?.message ||
        err?.response?.data?.Message ||
        "Unable to load employees. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [search, pageNumber]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchEmployees();
    }, search ? 400 : 0);

    return () => clearTimeout(timer);
  }, [fetchEmployees, search]);

  const handleSearch = (value) => {
    setSearch(value);
    setPageNumber(1);
  };

  const openSideDrawer = (action, employee) => {
    setSideDrawer({
      action,
      employee,
    });
  };

  // Adjust field names here to match the API response.
  const getEmployeeName = (employee) =>
    employee?.EmployeeName ??
    employee?.FullName ??
    employee?.Name ??
    employee?.employeeName ??
    "Unnamed Employee";

  const getEmployeeEmail = (employee) =>
    employee?.Email ??
    employee?.EmailId ??
    employee?.email ??
    "";

  const getEmployeeId = (employee) =>
    employee?.EmployeeId ??
    employee?.EmpId ??
    employee?.Id ??
    employee?.id;

  const getInitials = (name) =>
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase();

  return (
    <>
      <div className="search role-bx">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>

        <input
          id="mainSearch"
          value={search}
          placeholder="Search people..."
          onChange={(e) => handleSearch(e.target.value)}
        />
      </div>

      <div className="list role_list" id="listArea">
        {loading && (
          <div className="list-message">
            Loading employees...
          </div>
        )}

        {!loading && error && (
          <div className="list-message error">
            {error}
            <button onClick={fetchEmployees}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && employees.length === 0 && (
          <div className="list-message">
            No employees found.
          </div>
        )}

        {!loading &&
          !error &&
          employees.map((employee, index) => {
            const name = getEmployeeName(employee);
            const employeeId = getEmployeeId(employee);
            const email = getEmployeeEmail(employee);

            return (
              <div
                className="list-rolerow"
                key={employeeId ?? index}
              >
                <div className="av">
                  {/* {getInitials(name)} */}
                  <img src={employee?.ProfileImage} alt={employee?.ProfileImage}/>
                </div>

                <div className="who">
                  <div className="nm">{name}</div>

                  <div className="mt">
                    {employee?.Designation ??
                      employee?.JobTitle ??
                      employee?.DesignationName ??
                      "Employee"}
                    {email && ` · ${email}`}
                  </div>
                </div>

                <div className="stats">
                  <button
                    className="stat"
                    onClick={() =>
                      openSideDrawer("roles", employee)
                    }
                  >
                    <span className="lbl">Roles</span>
                    <span className="n">
                      {employee?.RoleCount ??
                        employee?.RolesCount ??
                        employee?.rolesCount ??
                        0}
                    </span>
                  </button>

                  <button
                    className="stat"
                    onClick={() =>
                      openSideDrawer("permissions", employee)
                    }
                  >
                    <span className="lbl">Permissions</span>
                    <span className="n">
                      {employee?.PermissionCount ??
                        employee?.PermissionsCount ??
                        employee?.permissionsCount ??
                        0}
                    </span>
                  </button>
                </div>
              </div>
            );
          })}
      </div>

      {!loading && !error && totalCount > 0 && (
        <div className="employee-pagination">
          <span>
            Page {pageNumber} · {totalCount} employees
          </span>

          <div>
            <button
              disabled={pageNumber === 1}
              onClick={() =>
                setPageNumber((page) => page - 1)
              }
            >
              Previous
            </button>

            <button
              disabled={pageNumber * PAGE_SIZE >= totalCount}
              onClick={() =>
                setPageNumber((page) => page + 1)
              }
            >
              Next
            </button>
          </div>
        </div>
      )}

      {sideDrawer && (
        <AccessSideDrawer
          action={sideDrawer.action}
          employee={sideDrawer.employee}
          onClose={() => setSideDrawer(null)}
        />
      )}
    </>
  );
};

export default EmployeesList;
